import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useViewMatch } from '../hooks/useViewMatch';
import { AppHeader } from './AppHeader';
import { Notification } from './Notification';
import { ConfirmModal } from './ConfirmModal';
import { HelpModal } from './HelpModal';
import { useConfirmModal } from '../hooks/useConfirmModal';
import { TeamSuggestionsModal, TeamSuggestion } from './ViewMatch/TeamSuggestionsModal';
import { getApiUrl } from '../config';
import * as analytics from '../lib/analytics';
import {
  ImprovedMatchHeader,
  ImprovedMatchActions,
  Team,
  JoinModal,
  Substitutes,
  ReportModal,
  TransferAdminModal,
  EditLocationModal,
  JoinOptionsModal,
  SelfReportModal,
  ValidationModal,
  EditTeamNamesModal,
  FinalScoreModal,
  ImportListModal,
  GuestRequestsModal,
  ManageGuestsModal,
} from './ViewMatch/index';
import { GroupAssociationSection } from './match/GroupAssociationSection';
import { WebSoccerField } from './ViewMatch/WebSoccerField';
import { SpecificHelpModal } from './Help';
import { CustomDragPreview } from './ViewMatch/CustomDragPreview';
import { EditPlayerModal } from './ViewMatch/EditPlayerModal';
import { CancelMatchModal } from './ViewMatch/CancelMatchModal';
import { CreateReview } from './CreateReview';
import { ReviewsList } from './ReviewsList';
import { PlayerReviewsModal } from './ViewMatch/PlayerReviewsModal';
import { PlayerProfileModal } from './PlayerProfileModal';
import { PlayerActionsModal } from './PlayerActionsModal';
import { MovePlayerModal } from './ViewMatch/MovePlayerModal';
import type { Player } from '../types';
import { ShareModal } from './ViewMatch/ShareModal';
import { InvitePlayerModal } from './ViewMatch/InvitePlayerModal';
import { PlayerStatsInlineEditor } from './ViewMatch/PlayerStatsInlineEditor';
import { computeSpotOpenBanner } from '../utils/spotOpenBanner';
import MatchJoinApplicationsModal from './MatchJoinApplicationsModal';
import ValidateMatchButton from './match/ValidateMatchButton';
import { useFeatureFlags } from '../contexts/FeatureFlagContext';

const ViewMatch: React.FC = () => {
  const { modal, hideConfirm } = useConfirmModal();
  const { id } = useParams();
  const navigate = useNavigate();
  const { venue_rewards: venueRewardsEnabled } = useFeatureFlags();

  // Team suggestions state
  const [showTeamSuggestionsModal, setShowTeamSuggestionsModal] = useState(false);
  const [teamSuggestions, setTeamSuggestions] = useState<TeamSuggestion[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  // PlayerActionsModal (tap-player menu)
  const [playerActionsModalPlayer, setPlayerActionsModalPlayer] = useState<Player | null>(null);
  const [movePlayerModalPlayer, setMovePlayerModalPlayer] = useState<Player | null>(null);

  // Public match join applications (organizer)
  const [showJoinApplicationsModal, setShowJoinApplicationsModal] = useState(false);

  // Cancelar partido (causas externas)
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancellingMatch, setCancellingMatch] = useState(false);

  // Inline stats editor state
  const [statsMode, setStatsMode] = useState(false);
  const [localStats, setLocalStats] = useState<Record<string, { goals: number; assists: number; saves: number }>>({});
  const [savingAllStats, setSavingAllStats] = useState(false);
  
  // Use the refactored hook - this replaces ALL the state and logic from the original component
  const {
    // Data
    match,
    teams,
    matchReport,
    matchScore: _matchScore,
    reportCompleted,
    userInfo: _userInfo,
    userIsCreator,
    isLoadingUser: _isLoadingUser,
    
    // User and UI
    user,
    isGuest,
    isMobile,
    isCompact,
    canViewTactics,
    
    // Notifications
    notification,
    showNotification,
    hideNotification,
    
    // Drag and Drop
    isDragging,
    draggedPlayer,
    dragOverPlayer,
    showDragPreview,
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleDrop,
    
    // Modals
    showHelpModal,
    showContextualHelp,
    showJoinModal,
    showJoinOptionsModal,
    showJoinOptionsForNewUser,
    hasUserClosedModal: _hasUserClosedModal,
    showReportModal,
    showSelfReportModal,
    showValidationModal,
    showTransferAdminModal,
    showEditLocationModal,
    showEditPlayerModal,
    showEditTeamNamesModal,
    showFinalScoreModal,
    showImportListModal,
    showCreateReviewModal,
    showPlayerReviewsModal,
    showPlayerProfileModal,
    showReviewsList,
    showVoteModal: _showVoteModal,
    showTacticalField,
    showShareModal,
    showGuestRequestsModal,
    setShowGuestRequestsModal,
    showManageGuestsModal,
    setShowManageGuestsModal,
    showInvitePlayerModal,
    setShowInvitePlayerModal,
    setShowHelpModal,
    setShowContextualHelp,
    setShowJoinModal: _setShowJoinModal,
    setShowJoinOptionsModal,
    setShowJoinOptionsForNewUser,
    setHasUserClosedModal: _setHasUserClosedModal,
    setShowReportModal,
    setShowSelfReportModal,
    setShowValidationModal,
    setShowTransferAdminModal,
    setShowEditLocationModal,
    setShowEditPlayerModal,
    setShowEditTeamNamesModal,
    setShowFinalScoreModal,
    setShowImportListModal,
    setShowCreateReviewModal: _setShowCreateReviewModal,
    setShowPlayerReviewsModal: _setShowPlayerReviewsModal,
    setShowPlayerProfileModal: _setShowPlayerProfileModal,
    setShowReviewsList: _setShowReviewsList,
    setShowVoteModal,
    setShowTacticalField,
    setShowShareModal,
    closeJoinModal,
    closeAllModals: _closeAllModals,
    resetModalState: _resetModalState,
    
    // Match Actions
    finalizingMatch: _finalizingMatch,
    finalizingReport: _finalizingReport,
    uploadingFinalScore,
    loadingReport,
    transferringAdmin,
    editingLocation,
    editingTeamNames,
    editingPlayerLoading,
    joiningMatch,
    isJoiningProcess: _isJoiningProcess,
    editingPlayer,
    joinAsSubstitute,
    selectedTeamIndex: _selectedTeamIndex,
    selectedPositionIndex: _selectedPositionIndex,
    occupyingSpaces,
    finalizeMatch,
    finalizeReport,
    uploadFinalScore,
    recalculateMatchResult,
    loadReport,
    removePlayer,
    selfRemove,
    updatePlayer,
    handleEditPlayer,
    transferAdmin,
    updateLocation,
    updateTeamNames,
    openJoinModal,
    closeJoinModal: _closeJoinModalAction,
    handleJoinSubmit,
    handleReplacePlayer,
    getSpaceKey,
    setEditingPlayer: _setEditingPlayer,
    setJoinAsSubstitute: _setJoinAsSubstitute,
    setSelectedTeamIndex: _setSelectedTeamIndex,
    setSelectedPositionIndex: _setSelectedPositionIndex,
    setOccupyingSpaces: _setOccupyingSpaces,
    
    // Self Reports
    selfReports,
    pendingValidation,
    submittingSelfReport,
    validatingReport,
    fetchSelfReports: _fetchSelfReports,
    submitSelfReport,
    validateReport,
    handleValidateStats,
    setPendingValidation: _setPendingValidation,
    canSubmitSelfReport: _canSubmitSelfReport,
    hasPendingValidation,
    hasRejectedReport,
    hasValidatedReport,
    getSelfReportStats,
    getValidatorName,
    getRejectionInfo,
    hasPlayerPendingStats,
    
    // Reviews
    hasReviewedPlayer,
    loadingReviews,
    canViewReviews,
    selectedPlayerForReview,
    selectedPlayerForViewReviews,
    selectedPlayerForProfile,
    loadReviewedPlayers: _loadReviewedPlayers,
    checkCanViewReviews: _checkCanViewReviews,
    handleCreateReview,
    handleReviewCreated,
    handleCloseCreateReview,
    handleViewPlayerReviews,
    handleClosePlayerReviews,
    handleCloseReviewsList,
    handleViewPlayerProfile,
    handleClosePlayerProfile,
    setSelectedPlayerForReview: _setSelectedPlayerForReview,
    setSelectedPlayerForViewReviews: _setSelectedPlayerForViewReviews,
    setSelectedPlayerForProfile: _setSelectedPlayerForProfile,
    
    // Calculations
    playerStatsMap: _playerStatsMap,
    calculatedMatchScore,
    mvpPlayerIds,
    topScorerPlayerIds,
    topAssisterPlayerIds,
    topSaverPlayerIds,
    winningTeamIndex,
    isDraw,
    getCombinedPlayerStats: _getCombinedPlayerStats,
    combinedPlayerStatsMap,
    hasGuestPlayers,
    guestPlayers: _guestPlayers,
    
    // Utilities
    clearGuestJoinState: _clearGuestJoinState,
    handleLoginClick,
    handleImportList,
    handleImportSuccess,
    handleJoinPosition,
    
    // WebSocket
    occupySpace: _occupySpace,
    releaseSpace: _releaseSpace,
    isConnected: _isConnected,
    joinMatch: _joinMatch,
    
    // Optimistic Updates
    optimisticUpdates: _optimisticUpdates,
    setOptimisticUpdates: _setOptimisticUpdates,
    applyOptimisticUpdate: _applyOptimisticUpdate,
    getOptimisticMatch: _getOptimisticMatch,
    getOptimisticPlayer: _getOptimisticPlayer,
    clearOptimisticUpdates: _clearOptimisticUpdates,
    hasPendingOperations: _hasPendingOperations,
    getPendingOperations: _getPendingOperations,
    
    // Team Synchronization
    syncTeamsWithBackend,

    // Player Swapping
    swapPlayers,
    movePlayer,

    // Guest requests
    pendingGuestRequestsCount,
    fetchPendingGuestRequestsCount,

    // Match data utils
    fetchMatch,
  } = useViewMatch();

  const isUserInMatch = user
    ? match?.players.some(p => p.userId === user.id) || match?.substitutes.some(p => p.userId === user.id)
    : false;

  const getAuthHeaders = (): Record<string, string> => {
    const token = localStorage.getItem('authToken');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return headers;
  };

  const handleConfirmPayment = async (playerId: string) => {
    if (!id) return;
    try {
      const res = await fetch(getApiUrl(`/api/matches/${id}/payment/confirm`), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ playerId }),
      });
      const data = await res.json();
      if (res.ok) {
        showNotification(data.message || 'Pago confirmado', 'success');
        fetchMatch();
      } else {
        showNotification(data.error || 'Error confirmando pago', 'error');
      }
    } catch {
      showNotification('Error confirmando pago', 'error');
    }
  };

  const handleCancelMatch = async (reason: string) => {
    if (!id) return;
    setCancellingMatch(true);
    try {
      const res = await fetch(getApiUrl(`/api/matches/${id}/cancel`), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ reason: reason.trim() }),
      });
      const data = await res.json();
      if (res.ok) {
        const notified = data?.notified ?? 0;
        showNotification(
          notified > 0
            ? `Partido cancelado. Se avisó a ${notified} jugador${notified === 1 ? '' : 'es'}.`
            : 'Partido cancelado.',
          'success'
        );
        setShowCancelModal(false);
        fetchMatch();
      } else {
        showNotification(data.error || 'Error al cancelar el partido', 'error');
      }
    } catch {
      showNotification('Error al cancelar el partido', 'error');
    } finally {
      setCancellingMatch(false);
    }
  };

  const handlePromoteSubstitute = async (playerId: string) => {
    try {
      await swapPlayers(playerId, playerId);
      showNotification('Jugador promovido a titular', 'success');
    } catch {
      // Error already shown by swapPlayers
    }
  };

  const handleRepeatMatch = () => {
    if (!match) return;
    const formData = {
      location: match.location || '',
      mapsUrl: match.mapsUrl || '',
      time: '',
      date: '',
      maxPlayers: match.maxPlayers || 5,
      price: match.price != null ? String(match.price) : '',
      team1Name: match.team1Name || 'Equipo 1',
      team2Name: match.team2Name || 'Equipo 2',
    };
    localStorage.setItem('landingFormData', JSON.stringify(formData));
    navigate('/create-match');
  };

  const handleSuggestTeams = async () => {
    if (!id) return;
    const playerCount = match?.players?.length ?? 0;
    const hasSkills = !!match?.players?.some((p: any) => p.organizerSkillScore != null);
    analytics.track('team_suggest_clicked', { matchId: id, playerCount, hasSkills });
    setLoadingSuggestions(true);
    try {
      const res = await fetch(getApiUrl(`/api/matches/${id}/suggest-teams`), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ count: 3 }),
      });
      const data = await res.json();
      if (res.ok) {
        setTeamSuggestions(data.suggestions || []);
        setShowTeamSuggestionsModal(true);
      } else {
        showNotification(data.error || 'Error al generar sugerencias', 'error');
      }
    } catch {
      showNotification('Error de red', 'error');
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const handleApplySuggestion = async (suggestion: TeamSuggestion) => {
    if (!id) return;
    setLoadingSuggestions(true);
    try {
      const res = await fetch(getApiUrl(`/api/matches/${id}/generate-teams`), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          suggestion: {
            team1Ids: suggestion.team1.map((p) => p.id),
            team2Ids: suggestion.team2.map((p) => p.id),
            subsIds: (suggestion.subs || []).map((p) => p.id),
          },
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setShowTeamSuggestionsModal(false);
        showNotification('¡Equipos aplicados!', 'success');
        fetchMatch();
      } else {
        showNotification(data.error || 'Error al aplicar equipos', 'error');
      }
    } catch {
      showNotification('Error de red', 'error');
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const handleSetPlayerSkill = async (playerId: string, score: number) => {
    if (!id) return;
    try {
      const res = await fetch(getApiUrl(`/api/matches/${id}/players/${playerId}/organizer-skill`), {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ score }),
      });
      if (!res.ok) {
        const data = await res.json();
        showNotification(data.error || 'Error al guardar puntaje', 'error');
      } else {
        fetchMatch();
      }
    } catch {
      showNotification('Error de red', 'error');
    }
  };

  const handleLinkFriends = async (playerAId: string, playerBId: string) => {
    if (!id) return;
    try {
      const res = await fetch(getApiUrl(`/api/matches/${id}/players/link-friends`), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ playerAId, playerBId }),
      });
      if (res.ok) {
        fetchMatch();
      } else {
        const data = await res.json();
        showNotification(data.error || 'Error al vincular', 'error');
      }
    } catch {
      showNotification('Error de red', 'error');
    }
  };

  const handleUnlinkFriend = async (playerId: string) => {
    if (!id) return;
    try {
      const res = await fetch(getApiUrl(`/api/matches/${id}/players/${playerId}/friend-link`), {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        fetchMatch();
      } else {
        const data = await res.json();
        showNotification(data.error || 'Error al desvincular', 'error');
      }
    } catch {
      showNotification('Error de red', 'error');
    }
  };

  const handleGenerateReplacementLink = async (player: Player) => {
    if (!match) return;
    try {
      const res = await fetch(getApiUrl(`/api/replacement-tokens`), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ matchId: match.id, targetPlayerId: player.id }),
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.error || 'Error al generar link');
        return;
      }
      await navigator.clipboard.writeText(data.url);
      alert('¡Link copiado! Mandáselo al jugador que va a tomar el lugar.');
    } catch {
      alert('Error al generar link de reemplazo');
    }
  };

  // Build report status map for WebSoccerField badges
  const reportStatusMap = useMemo(() => {
    if (!match || !selfReports) return {};
    const map: Record<string, { pending: boolean; approved: boolean; rejected: boolean; unsubmitted: boolean }> = {};
    const allPlayers = [...match.players, ...(match.substitutes || [])];
    allPlayers.forEach(p => {
      const report = selfReports[p.id];
      map[p.id] = {
        pending: report?.validationStatus === 'PENDING',
        approved: report?.validationStatus === 'APPROVED',
        rejected: report?.validationStatus === 'REJECTED',
        unsubmitted: match.status === 'FINISHED' && !!p.userId && !report,
      };
    });
    return map;
  }, [match, selfReports]);

  // Inline stats editor handlers
  const handleEnterStatsMode = () => {
    const allPlayers = [...(match?.players || []), ...(match?.substitutes || [])];
    const initial: Record<string, { goals: number; assists: number; saves: number }> = {};
    allPlayers.forEach(p => {
      const existing = (matchReport as any)?.stats?.find((s: any) => s.playerId === p.id);
      initial[p.id] = { goals: existing?.goals ?? 0, assists: existing?.assists ?? 0, saves: existing?.saves ?? 0 };
    });
    setLocalStats(initial);
    setStatsMode(true);
  };

  const handleLocalStatsChange = (playerId: string, field: 'goals' | 'assists' | 'saves', delta: number) => {
    setLocalStats(prev => ({
      ...prev,
      [playerId]: {
        ...prev[playerId],
        [field]: Math.max(0, (prev[playerId]?.[field] ?? 0) + delta),
      },
    }));
  };

  const handleSubmitAllStats = async () => {
    setSavingAllStats(true);
    try {
      const allPlayers = [...(match?.players || []), ...(match?.substitutes || [])];
      const playerNameMap = Object.fromEntries(allPlayers.map(p => [p.id, p.name]));
      const stats = Object.entries(localStats).map(([playerId, s]) => ({
        playerId,
        name: playerNameMap[playerId] || '',
        goals: s.goals,
        assists: s.assists,
        saves: s.saves,
        votes: 0,
        votePercentage: 0,
      }));
      await loadReport(stats);
      setStatsMode(false);
    } finally {
      setSavingAllStats(false);
    }
  };

  // Build player stats map for WebSoccerField (goals/assists/saves)
  const fieldPlayerStatsMap = useMemo(() => {
    if (!combinedPlayerStatsMap) return {};
    const map: Record<string, { goals: number; assists: number; saves: number }> = {};
    Object.entries(combinedPlayerStatsMap).forEach(([id, stats]) => {
      if (stats) {
        map[id] = { goals: stats.goals || 0, assists: stats.assists || 0, saves: stats.saves || 0 };
      }
    });
    return map;
  }, [combinedPlayerStatsMap]);

  // Build payment status map for WebSoccerField
  const fieldPaymentStatusMap = useMemo(() => {
    if (!match?.payments) return undefined;
    const map: Record<string, 'confirmed' | 'cash' | 'transfer' | 'pending'> = {};
    const allPlayers = [...match.players, ...(match.substitutes || [])];
    allPlayers.forEach(p => {
      const payment = match.payments?.[p.id];
      if (!payment) {
        map[p.id] = 'pending';
      } else if (payment.confirmedByAdmin) {
        map[p.id] = 'confirmed';
      } else if (payment.claimedPaid) {
        map[p.id] = 'transfer';
      } else {
        map[p.id] = 'pending';
      }
    });
    return map;
  }, [match]);

  // If no match loaded yet
  if (!match) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        backgroundColor: '#181A1B',
        color: '#FFFFFF'
      }}>
        <div>Loading match...</div>
      </div>
    );
  }

  return (
    <div style={{ 
      fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
      margin: isMobile ? '8px 0 8px 0' : '20px',
      backgroundColor: '#181A1B',
      minHeight: '100vh',
      color: '#FFFFFF',
      paddingLeft: isMobile ? '4px' : '20px',
      paddingRight: isMobile ? '4px' : '20px',
      paddingTop: isMobile ? '65px' : '0px',
      paddingBottom: '0px'
    }}>
      <AppHeader title="Ver Partido" />
      
      <div style={{ marginTop: '20px' }}>
        <Notification
          message={notification.message}
          type={notification.type}
          isVisible={notification.isVisible}
          onClose={hideNotification}
          action={notification.action}
          durationMs={notification.durationMs}
        />
      </div>
      
      <ConfirmModal
        isOpen={modal.isOpen}
        title={modal.title}
        message={modal.message}
        confirmText={modal.confirmText}
        cancelText={modal.cancelText}
        confirmButtonColor={modal.confirmButtonColor}
        onConfirm={modal.onConfirm || (() => {})}
        onCancel={modal.onCancel || hideConfirm}
      />

      {/* Main content */}
      {teams && (
        <div style={{
          backgroundColor: '#23272A',
          padding: isMobile ? '4px 2px' : '4px',
          borderRadius: '18px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
          marginBottom: 0,
          border: '1px solid #33383B',
        }}>
              <ImprovedMatchHeader
            match={match}
                userIsCreator={!!userIsCreator}
                onEditLocation={() => setShowEditLocationModal(true)}
                matchScore={calculatedMatchScore}
                reportCompleted={reportCompleted}
                compact
              />

              <GroupAssociationSection
                matchId={match.id}
                currentGroupName={(match as any).currentGroupName}
              />

              {/* Validar el partido en la cancha. Solo con venue_rewards
                  prendido (sin el flag el server responde 404 al pedir el
                  código), solo el organizador, y solo si el partido no está
                  cancelado ni validado. */}
              {venueRewardsEnabled && userIsCreator && match.status !== 'CANCELLED' && !(match as any).validation && (
                <ValidateMatchButton matchId={match.id} />
              )}

              <ImprovedMatchActions
            match={match}
                userIsCreator={userIsCreator}
                onShowNotification={showNotification}
                onFinishMatch={finalizeMatch}
                hasReport={!!matchReport}
                onTransferAdmin={() => setShowTransferAdminModal(true)}
                currentUser={user}
                reportCompleted={reportCompleted}
                onFinalizeReport={finalizeReport}
                onCreateReview={handleCreateReview}
                onUploadFinalScore={() => setShowFinalScoreModal(true)}
                onLoadReport={handleEnterStatsMode}
                onVote={() => setShowVoteModal(true)}
                onEditLocation={() => setShowEditLocationModal(true)}
                hasGuestPlayers={hasGuestPlayers}
            canVote={false} // This would need to be implemented
                isMobile={isMobile}
                onRecalculateMatchResult={recalculateMatchResult}
                onImportList={handleImportList}
                onSyncTeams={syncTeamsWithBackend}
                onToggleTactics={() => setShowTacticalField(!showTacticalField)}
                showTacticalField={showTacticalField}
                onSetTacticalField={setShowTacticalField}
                canViewTactics={!!(canViewTactics && teams)}
                onReportStats={() => setShowSelfReportModal(true)}
                setShowShareModal={setShowShareModal}
                pendingGuestRequestsCount={pendingGuestRequestsCount}
                onViewGuestRequests={userIsCreator ? () => setShowGuestRequestsModal(true) : undefined}
                onManageGuests={(userIsCreator || isUserInMatch) && match.status === 'SCHEDULED' ? () => setShowManageGuestsModal(true) : undefined}
                onViewJoinApplications={userIsCreator && match.isPublic ? () => setShowJoinApplicationsModal(true) : undefined}
                isUserInMatch={!!isUserInMatch}
                onSuggestTeams={userIsCreator && match.status === 'SCHEDULED' ? handleSuggestTeams : undefined}
                onRepeatMatch={match.status === 'FINISHED' ? handleRepeatMatch : undefined}
                onCancelMatch={userIsCreator && match.status === 'SCHEDULED' ? () => setShowCancelModal(true) : undefined}
              />

          {/* Banner de partido cancelado: estado claro + motivo del organizador. */}
          {match.status === 'CANCELLED' && (
            <div style={{
              display: 'flex',
              gap: 10,
              padding: '12px 14px',
              marginBottom: 14,
              background: 'rgba(244, 67, 54, 0.10)',
              border: '1px solid rgba(244, 67, 54, 0.55)',
              borderRadius: 14,
            }}>
              <span style={{ fontSize: 20 }}>❌</span>
              <div style={{ flex: 1 }}>
                <div style={{ color: '#F44336', fontWeight: 700, fontSize: 15 }}>
                  Partido cancelado
                </div>
                {!!(match as any).cancellationReason && (
                  <div style={{ color: '#E0E0E0', fontSize: 13, marginTop: 3, lineHeight: 1.4 }}>
                    {(match as any).cancellationReason}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Banner: lugar crítico disponible (partido de grupo, < 4h, jugador confirmado) */}
          {match.status === 'SCHEDULED' && user && (() => {
            const { show, hoursUntilMatch } = computeSpotOpenBanner(match, user.id);
            if (!show) return null;

            // Cache-buster para que WhatsApp re-escrapee el preview (no use cache viejo por URL).
            const shareUrl = `${process.env.REACT_APP_CLIENT_URL || window.location.origin}/join/${match.id}?t=${Date.now()}`;
            const canNativeShare = !!(navigator.share && typeof navigator.share === 'function');

            const handleShareSpot = async () => {
              const text = `⚽ Hay un lugar libre en el partido de hoy en ${match.location}. ¿Podés ir?`;
              if (canNativeShare) {
                try {
                  await navigator.share({ title: `Partido en ${match.location}`, text, url: shareUrl });
                } catch { /* user cancelled */ }
              } else {
                try {
                  await navigator.clipboard.writeText(shareUrl);
                  showNotification('✅ Link copiado al portapapeles', 'success');
                } catch {
                  showNotification('Copiá el link: ' + shareUrl, 'info');
                }
              }
            };

            return (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                padding: '12px 14px',
                marginBottom: 14,
                background: 'rgba(255, 140, 0, 0.08)',
                border: '1px solid rgba(255, 140, 0, 0.55)',
                borderRadius: 14,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 18 }}>⚠️</span>
                  <div>
                    <div style={{ fontWeight: 700, color: '#FF8C00', fontSize: isMobile ? 14 : 15 }}>
                      Se bajó un jugador · 1 lugar disponible
                    </div>
                    <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, marginTop: 2 }}>
                      Quedan {Math.ceil(hoursUntilMatch)}h para el partido
                    </div>
                  </div>
                </div>
                <button
                  onClick={handleShareSpot}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                    padding: '10px 16px',
                    background: '#FF8C00',
                    color: '#000',
                    border: 'none',
                    borderRadius: 10,
                    cursor: 'pointer',
                    fontWeight: 700,
                    fontSize: isMobile ? 14 : 13,
                    width: '100%',
                  }}
                >
                  🔗 Compartir link del partido
                </button>
              </div>
            );
          })()}

          {/* CTA: calificar a todos rápido (batch review wizard) */}
          {match.status === 'FINISHED' && user && teams && (() => {
            const allPlayers = [
              ...(teams.team1 || []).filter((p): p is NonNullable<typeof p> => p != null),
              ...(teams.team2 || []).filter((p): p is NonNullable<typeof p> => p != null),
            ];
            const userIsInMatch = allPlayers.some(p => p.userId === user?.id);
            if (!userIsInMatch) return null;
            const otherPlayers = allPlayers.filter(p => p.userId && p.userId !== user.id);
            if (otherPlayers.length === 0) return null;
            const reviewedCount = otherPlayers.filter(p => hasReviewedPlayer[p.id]).length;
            if (reviewedCount >= otherPlayers.length) return null;
            return (
              <button
                type="button"
                onClick={() => navigate(`/match/${match.id}/rate`)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  gap: 12,
                  padding: isMobile ? '12px 14px' : '14px 18px',
                  marginBottom: 12,
                  background: 'linear-gradient(135deg, rgba(0, 230, 118, 0.15), rgba(0, 230, 118, 0.05))',
                  border: '1px solid rgba(0, 230, 118, 0.4)',
                  borderRadius: 14,
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                <span style={{ fontSize: isMobile ? 22 : 24 }}>⚡</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, color: '#00E676', fontSize: isMobile ? 14 : 15 }}>
                    Calificar a todos rápido
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: isMobile ? 12 : 13, marginTop: 2 }}>
                    {reviewedCount}/{otherPlayers.length} calificados · 60-90 seg
                  </div>
                </div>
                <span style={{ color: '#00E676', fontSize: 18 }}>›</span>
              </button>
            );
          })()}

          {/* Banner: desbloquear ver tus reviews dejando al menos 1 */}
          {match.status === 'FINISHED' && user && teams && (() => {
            const allPlayers = [
              ...(teams.team1 || []).filter((p): p is NonNullable<typeof p> => p != null),
              ...(teams.team2 || []).filter((p): p is NonNullable<typeof p> => p != null),
              ...(match?.substitutes || [])
            ];
            const userIsInMatch = allPlayers.some(p => p.userId === user?.id);
            const needsOneReviewToUnlock = userIsInMatch && !canViewReviews;
            if (!needsOneReviewToUnlock) return null;
            return (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: isMobile ? 10 : 14,
                  padding: isMobile ? '12px 10px' : '14px 16px',
                  marginBottom: 16,
                  background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.15), rgba(255, 165, 0, 0.1))',
                  border: '1px solid rgba(255, 215, 0, 0.4)',
                  borderRadius: 14,
                }}
              >
                <span style={{ fontSize: isMobile ? 22 : 24 }}>⭐</span>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontWeight: 700, color: '#FFD700', fontSize: isMobile ? 15 : 16 }}>
                    Dejá tus reviews
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: isMobile ? 12 : 13, marginTop: 2 }}>
                    Dejá al menos 1 review para desbloquear las que te dejaron. Tocá un jugador para dejarle la tuya.
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Campo Táctico con FIFA Cards */}
          {showTacticalField && teams && canViewTactics && (
            <div
              data-testid="soccer-field-container"
              style={{
                width: '100%',
                marginBottom: 0,
              }}
            >
              <WebSoccerField
                team1={teams.team1}
                team2={teams.team2}
                team1Name={match?.team1Name || 'Equipo 1'}
                team2Name={match?.team2Name || 'Equipo 2'}
                team1Color={match?.team1Color || '#4CAF50'}
                team2Color={match?.team2Color || '#2196F3'}
                team1Goals={calculatedMatchScore?.team1Goals}
                team2Goals={calculatedMatchScore?.team2Goals}
                // Solo aplica cuando el marcador es el que cargó el organizador (no el
                // inferido de los goles de los jugadores). Misma condición que usa
                // useMatchCalculations para armar calculatedMatchScore: alcanza con que
                // uno de los dos sea número, así un 0 que llegue como null no lo apaga.
                scoreMode={
                  typeof match?.team1Goals === 'number' || typeof match?.team2Goals === 'number'
                    ? match?.scoreMode
                    : null
                }
                maxPlayers={match?.maxPlayers || 5}
                isFinished={match?.status === 'FINISHED'}
                onPlayerClick={(player) => {
                  setPlayerActionsModalPlayer(player);
                }}
                playerStatsMap={fieldPlayerStatsMap}
                mvpPlayerIds={mvpPlayerIds}
                reportStatusMap={reportStatusMap}
                paymentStatusMap={fieldPaymentStatusMap}
                currentUserId={user?.id}
                pricePerPlayer={match?.price && match?.maxPlayers ? Math.round(match.price / (match.maxPlayers * 2)) : undefined}
                onEmptySlotClick={(teamIndex, positionIndex) => {
                  setShowInvitePlayerModal(true);
                }}
                userIsCreator={!!userIsCreator}
                onFieldDragStart={handleDragStart}
                onFieldDragEnd={handleDragEnd}
                onFieldDragOver={handleDragOver}
                onFieldDrop={handleDrop}
                draggedPlayer={draggedPlayer}
                dragOverPlayer={dragOverPlayer}
                substitutes={match?.substitutes || []}
              />
            </div>
          )}

          {/* Botones de gestión */}

          {/* Botones adicionales para partido finalizado */}
          {match.status === 'FINISHED' && (
            <div style={{ 
              display: 'flex', 
              gap: '10px', 
              flexWrap: 'wrap', 
              justifyContent: 'center',
              marginBottom: '20px'
            }}>
              {/* Sistema de auto-reportes - Movido a ImprovedMatchActions */}

              {hasPendingValidation && (
                <button 
                  style={{
                    padding: isMobile ? '12px 16px' : '10px 20px',
                    backgroundColor: '#FF9800',
                    color: '#FFFFFF',
                    border: 'none',
                    borderRadius: isMobile ? '16px' : '12px',
                    cursor: 'pointer',
                    fontSize: isMobile ? '15px' : '14px',
                    fontWeight: '600',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.25)',
                    minHeight: isMobile ? '48px' : 'auto'
                  }}
                  onClick={() => setShowSelfReportModal(true)}
                >
                  ⏳ Mi Reporte Pendiente
                </button>
              )}

              {hasRejectedReport && (
                <button 
                  style={{
                    padding: isMobile ? '12px 16px' : '10px 20px',
                    backgroundColor: '#F44336',
                    color: '#FFFFFF',
                    border: 'none',
                    borderRadius: isMobile ? '16px' : '12px',
                    cursor: 'pointer',
                    fontSize: isMobile ? '15px' : '14px',
                    fontWeight: '600',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.25)',
                    minHeight: isMobile ? '48px' : 'auto'
                  }}
                  onClick={() => setShowSelfReportModal(true)}
                >
                  ❌ Reporte Rechazado - Reenviar
                </button>
              )}

              {hasValidatedReport && (
                <button 
                  style={{
                    padding: isMobile ? '12px 16px' : '10px 20px',
                    backgroundColor: '#4CAF50',
                    color: '#FFFFFF',
                    border: 'none',
                    borderRadius: isMobile ? '16px' : '12px',
                    cursor: 'pointer',
                    fontSize: isMobile ? '15px' : '14px',
                    fontWeight: '600',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.25)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: isMobile ? '6px' : '8px',
                    minHeight: isMobile ? '48px' : 'auto',
                    flexWrap: isMobile ? 'wrap' : 'nowrap'
                  }}
                  onClick={() => setShowSelfReportModal(true)}
                >
                  <span style={{ fontSize: isMobile ? '18px' : '16px' }}>✅</span>
                  <span style={{ 
                    fontSize: isMobile ? '14px' : '14px',
                    lineHeight: '1.2'
                  }}>Reporte Validado</span>
                  <span style={{ 
                    fontSize: isMobile ? '11px' : '12px', 
                    backgroundColor: 'rgba(255,255,255,0.2)', 
                    padding: isMobile ? '3px 8px' : '2px 6px', 
                    borderRadius: isMobile ? '10px' : '8px',
                    marginLeft: isMobile ? '2px' : '4px',
                    whiteSpace: 'nowrap'
                  }}>
                    Cargado
                  </span>
                </button>
              )}
            </div>
          )}

          {/* Vista de Partido - Solo se muestra cuando NO está en vista táctica */}
          {!showTacticalField && (
            <>
              {/* Desktop: single viewport-height container with teams + subs strip */}
              <div style={{
                ...(!(isMobile || isCompact) ? {
                  height: 'calc(100vh - 160px)',
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%',
                } : {}),
              }}>
              <div style={{
                display: 'flex',
                gap: isMobile || isCompact ? '12px' : '20px',
                flexDirection: isMobile || isCompact ? 'column' : 'row',
                flexWrap: 'nowrap',
                marginBottom: isMobile || isCompact ? '20px' : '0',
                width: '100%',
                ...(!(isMobile || isCompact) ? {
                  flex: 1,
                  minHeight: 0,
                  alignItems: 'stretch',
                } : {}),
              }}>
            <Team
              teamId="team1"
              teamName={match?.team1Name || 'Equipo 1'}
              players={teams.team1 || []}
              backgroundColor={`${match?.team1Color || '#4CAF50'}1A`}
              borderColor={match?.team1Color || '#4CAF50'}
              titleColor={match?.team1Color || '#4CAF50'}
              teamIndex={0}
              userIsCreator={!!userIsCreator}
              occupyingSpaces={occupyingSpaces}
              onRemovePlayer={removePlayer}
              onSelfRemove={selfRemove}
              onEditPlayer={handleEditPlayer}
              onCreateReview={handleCreateReview}
              onViewReviews={handleViewPlayerReviews}
              onViewProfile={handleViewPlayerProfile}
              currentUserId={user?.id}
              canViewReviews={canViewReviews}
              onJoinPosition={handleJoinPosition}
              getSpaceKey={getSpaceKey}
              playerStats={combinedPlayerStatsMap}
              matchStatus={match.status}
              mvpPlayerIds={mvpPlayerIds}
              topScorerPlayerIds={topScorerPlayerIds}
              topAssisterPlayerIds={topAssisterPlayerIds}
              topSaverPlayerIds={topSaverPlayerIds}
              creatorId={match.creatorId}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              isDragging={isDragging}
              draggedPlayer={draggedPlayer}
              dragOverPlayer={dragOverPlayer}
              hasReviewedPlayer={hasReviewedPlayer}
              loadingReviews={loadingReviews}
              hasPendingStats={Object.fromEntries(
                match.players.map(p => [p.id, hasPlayerPendingStats(p.userId || '')])
              )}
              onValidateStats={handleValidateStats}
              onEditTeamNames={() => setShowEditTeamNamesModal(true)}
              isWinningTeam={winningTeamIndex === 0}
              teamGoals={calculatedMatchScore?.team1Goals}
              isDraw={isDraw}
              allPlayers={match.players}
              onSetPlayerSkill={userIsCreator ? handleSetPlayerSkill : undefined}
              onLinkFriends={userIsCreator ? handleLinkFriends : undefined}
              onUnlinkFriend={userIsCreator ? handleUnlinkFriend : undefined}
              onGenerateReplacementLink={handleGenerateReplacementLink}
            />

            <Team
              teamId="team2"
              teamName={match?.team2Name || 'Equipo 2'}
              players={teams.team2 || []}
              backgroundColor={`${match?.team2Color || '#2196F3'}1A`}
              borderColor={match?.team2Color || '#2196F3'}
              titleColor={match?.team2Color || '#2196F3'}
              teamIndex={1}
              userIsCreator={!!userIsCreator}
              occupyingSpaces={occupyingSpaces}
              onRemovePlayer={removePlayer}
              onSelfRemove={selfRemove}
              onEditPlayer={handleEditPlayer}
              onCreateReview={handleCreateReview}
              onViewReviews={handleViewPlayerReviews}
              onViewProfile={handleViewPlayerProfile}
              currentUserId={user?.id}
              canViewReviews={canViewReviews}
              onJoinPosition={handleJoinPosition}
              getSpaceKey={getSpaceKey}
              playerStats={combinedPlayerStatsMap}
              matchStatus={match.status}
              mvpPlayerIds={mvpPlayerIds}
              topScorerPlayerIds={topScorerPlayerIds}
              topAssisterPlayerIds={topAssisterPlayerIds}
              topSaverPlayerIds={topSaverPlayerIds}
              creatorId={match.creatorId}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              isDragging={isDragging}
              draggedPlayer={draggedPlayer}
              dragOverPlayer={dragOverPlayer}
              hasReviewedPlayer={hasReviewedPlayer}
              loadingReviews={loadingReviews}
              hasPendingStats={Object.fromEntries(
                match.players.map(p => [p.id, hasPlayerPendingStats(p.userId || '')])
              )}
              onValidateStats={handleValidateStats}
              onEditTeamNames={() => setShowEditTeamNamesModal(true)}
              isWinningTeam={winningTeamIndex === 1}
              teamGoals={calculatedMatchScore?.team2Goals}
              isDraw={isDraw}
              allPlayers={match.players}
              onSetPlayerSkill={userIsCreator ? handleSetPlayerSkill : undefined}
              onLinkFriends={userIsCreator ? handleLinkFriends : undefined}
              onUnlinkFriend={userIsCreator ? handleUnlinkFriend : undefined}
              onGenerateReplacementLink={handleGenerateReplacementLink}
            />
          </div>


          {/* Substitutes section */}
          <Substitutes
            substitutes={match.substitutes || []}
            userIsCreator={!!userIsCreator}
            onRemovePlayer={removePlayer}
            onSelfRemove={selfRemove}
            onEditPlayer={handleEditPlayer}
            onCreateReview={handleCreateReview}
            onViewReviews={handleViewPlayerReviews}
            onViewProfile={handleViewPlayerProfile}
            currentUserId={user?.id}
            canViewReviews={canViewReviews}
            onJoinAsSubstitute={() => {
              openJoinModal(true);
            }}
            playerStats={combinedPlayerStatsMap}
            matchStatus={match.status}
            mvpPlayerIds={mvpPlayerIds}
            topScorerPlayerIds={topScorerPlayerIds}
            topAssisterPlayerIds={topAssisterPlayerIds}
            topSaverPlayerIds={topSaverPlayerIds}
            creatorId={match.creatorId}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            isDragging={isDragging}
            draggedPlayer={draggedPlayer}
            dragOverPlayer={dragOverPlayer}
            hasReviewedPlayer={hasReviewedPlayer}
            loadingReviews={loadingReviews}
            hasPendingStats={Object.fromEntries(
              [...match.players, ...(match.substitutes || [])].map(p => [p.id, hasPlayerPendingStats(p.userId || '')])
            )}
            allPlayers={match.players}
            onSetPlayerSkill={userIsCreator ? handleSetPlayerSkill : undefined}
            onLinkFriends={userIsCreator ? handleLinkFriends : undefined}
            onUnlinkFriend={userIsCreator ? handleUnlinkFriend : undefined}
            compact={!(isMobile || isCompact)}
            onPromoteSubstitute={userIsCreator ? handlePromoteSubstitute : undefined}
            onGenerateReplacementLink={handleGenerateReplacementLink}
          />
          </div>
            </>
          )}

          {/* Inline stats editor (organizer, partido FINISHED) */}
          {statsMode && match && userIsCreator && match.status === 'FINISHED' && (
            <div style={{ padding: '0 16px 16px' }}>
              <PlayerStatsInlineEditor
                players={match.players || []}
                substitutes={match.substitutes || []}
                localStats={localStats}
                onChange={handleLocalStatsChange}
                onSave={handleSubmitAllStats}
                onCancel={() => setStatsMode(false)}
                saving={savingAllStats}
              />
            </div>
          )}
        </div>
      )}

      {/* Join Modals */}
      <JoinModal
        isOpen={showJoinModal}
        isSubstitute={joinAsSubstitute}
        onClose={closeJoinModal}
        onSubmit={handleJoinSubmit}
        isLoading={joiningMatch}
        isGuest={isGuest}
        onLoginClick={handleLoginClick}
        isCreatorAddingGuest={userIsCreator}
      />

      <JoinOptionsModal
        isOpen={showJoinOptionsModal}
        isSubstitute={false}
        onClose={() => {
          setShowJoinOptionsModal(false);
          closeJoinModal(); // ✅ Llamar closeJoinModal para establecer hasUserClosedModal = true
        }}
        onSubmit={handleJoinSubmit}
        onReplacePlayer={handleReplacePlayer}
        isLoading={joiningMatch}
        matchId={id || ''}
        players={match?.players || []}
        substitutes={match?.substitutes || []}
        maxPlayers={match?.maxPlayers || 5}
      />

      <JoinOptionsModal
        isOpen={showJoinOptionsForNewUser}
        isSubstitute={false}
        onClose={() => {
          setShowJoinOptionsForNewUser(false);
          closeJoinModal(); // ✅ Llamar closeJoinModal para establecer hasUserClosedModal = true
        }}
        onSubmit={handleJoinSubmit}
        onReplacePlayer={handleReplacePlayer}
        isLoading={joiningMatch}
        matchId={id || ''}
        players={match?.players || []}
        substitutes={match?.substitutes || []}
        maxPlayers={match?.maxPlayers || 5}
      />

      {/* Report Modal */}
      <ReportModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        onSubmit={async (stats) => {
          try {
            await loadReport(stats);
            setShowReportModal(false);
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Error submitting report:', error);
          }
        }}
        players={[...(match?.players || []), ...(match?.substitutes || [])]}
        isLoading={loadingReport}
        existingStats={matchReport?.stats || []}
      />

      {/* Admin Modals */}
      <TransferAdminModal
        isOpen={showTransferAdminModal}
        onClose={() => setShowTransferAdminModal(false)}
        onTransfer={transferAdmin}
        players={[...(match?.players || []), ...(match?.substitutes || [])]}
        currentUserId={user?.id || ''}
        loading={transferringAdmin}
      />

      <EditLocationModal
        isOpen={showEditLocationModal}
        onClose={() => setShowEditLocationModal(false)}
        onSave={async (data) => {
          try {
            await updateLocation(data);
            setShowEditLocationModal(false);
          } catch (error) {
            // El error ya se maneja en updateLocation
          }
        }}
        currentLocation={match?.location || ''}
        currentMapsUrl={match?.mapsUrl || ''}
        currentDate={match?.date || ''}
        currentTime={match?.time || ''}
        currentPrice={match?.price?.toString() || '0'}
        loading={editingLocation}
      />

      <EditTeamNamesModal
        isOpen={showEditTeamNamesModal}
        onClose={() => setShowEditTeamNamesModal(false)}
        onSave={(data) => {
          updateTeamNames(data);
        }}
        currentTeam1Name={match?.team1Name || 'Equipo 1'}
        currentTeam2Name={match?.team2Name || 'Equipo 2'}
        currentTeam1Color={match?.team1Color}
        currentTeam2Color={match?.team2Color}
        loading={editingTeamNames}
      />

      <EditPlayerModal
        isOpen={showEditPlayerModal}
        onClose={() => setShowEditPlayerModal(false)}
        player={editingPlayer}
        onSave={(playerId, position, rating, name, updateProfile) => {
          updatePlayer(playerId, position, rating, name, updateProfile);
        }}
        isLoading={editingPlayerLoading}
      />

      {/* Self Report Modal */}
                  <SelfReportModal
              isOpen={showSelfReportModal}
              onClose={() => setShowSelfReportModal(false)}
              onSubmit={submitSelfReport}
              isLoading={submittingSelfReport}
              existingStats={getSelfReportStats}
              isValidationPending={hasPendingValidation}
        validationStatus={hasValidatedReport ? 'APPROVED' : hasRejectedReport ? 'REJECTED' : 'PENDING'}
              validatorName={getValidatorName}
              rejectionInfo={getRejectionInfo}
            />

      {/* Validation Modal */}
      <ValidationModal
        isOpen={showValidationModal}
        onClose={() => setShowValidationModal(false)}
        onApprove={() => validateReport(true)}
        onReject={(reason) => validateReport(false, reason)}
        reporterName={pendingValidation?.reporterName || ''}
        stats={pendingValidation?.stats || { goals: 0, assists: 0, saves: 0 }}
        isLoading={validatingReport}
      />

      {/* Final Score Modal */}
      <FinalScoreModal
        isOpen={showFinalScoreModal}
        onClose={() => setShowFinalScoreModal(false)}
        onSubmit={async (team1Goals, team2Goals, scoreMode) => {
          try {
            await uploadFinalScore(team1Goals, team2Goals, scoreMode);
            setShowFinalScoreModal(false);
          } catch (error) {
            // El error ya se maneja en uploadFinalScore
          }
        }}
        isLoading={uploadingFinalScore}
        team1Name={match?.team1Name || 'Equipo 1'}
        team2Name={match?.team2Name || 'Equipo 2'}
        initialTeam1Goals={match?.team1Goals || 0}
        initialTeam2Goals={match?.team2Goals || 0}
        initialScoreMode={match?.scoreMode}
      />

      <CancelMatchModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onSubmit={handleCancelMatch}
        isLoading={cancellingMatch}
        matchLocation={match?.location}
      />

      {/* Help Modal */}
      <HelpModal
        isOpen={showHelpModal}
        onClose={() => setShowHelpModal(false)}
      />

      <SpecificHelpModal 
        isOpen={showContextualHelp} 
        onClose={() => setShowContextualHelp(false)} 
        context={match?.status === 'FINISHED' ? 'viewMatchFinished' : 'viewMatchScheduled'}
      />

      {/* Review Modals */}
      {showCreateReviewModal && selectedPlayerForReview && (
        <CreateReview
          matchId={id || ''}
          reviewedPlayer={selectedPlayerForReview}
          onReviewCreated={handleReviewCreated}
          onCancel={handleCloseCreateReview}
          isVisible={showCreateReviewModal}
        />
      )}

      {showPlayerReviewsModal && selectedPlayerForViewReviews && (
        <PlayerReviewsModal
          player={selectedPlayerForViewReviews}
          matchId={id || ''}
          isVisible={showPlayerReviewsModal}
          onClose={handleClosePlayerReviews}
        />
      )}

      {showPlayerProfileModal && selectedPlayerForProfile && (
        <PlayerProfileModal
          player={selectedPlayerForProfile}
          isVisible={showPlayerProfileModal}
          onClose={handleClosePlayerProfile}
        />
      )}

      {/* PlayerActionsModal — bottom-sheet menu cuando se toca un player */}
      {playerActionsModalPlayer && (() => {
        const p = playerActionsModalPlayer;
        const isFinished = match?.status === 'FINISHED';
        const isOwnPlayer = !!(user?.id && p.userId === user.id);
        return (
          <PlayerActionsModal
            isVisible={!!playerActionsModalPlayer}
            player={p}
            onClose={() => setPlayerActionsModalPlayer(null)}
            isOwnPlayer={isOwnPlayer}
            canEdit={(isOwnPlayer || !!userIsCreator) && !isFinished}
            canRemove={!!userIsCreator && !isFinished && !isOwnPlayer}
            canSelfRemove={isOwnPlayer && !userIsCreator && !isFinished}
            canMove={!!userIsCreator && match?.status !== 'CANCELLED'}
            onMove={() => setMovePlayerModalPlayer(p)}
            canCreateReview={isFinished && !!p.userId && !isOwnPlayer}
            hasReviewedPlayer={!!hasReviewedPlayer[p.id]}
            canViewReviews={!!canViewReviews}
            loadingReviews={loadingReviews}
            onViewProfile={p.user ? () => handleViewPlayerProfile(p) : undefined}
            onCreateReview={isFinished && !isOwnPlayer ? () => handleCreateReview(p) : undefined}
            onViewReviews={() => handleViewPlayerReviews(p)}
            onEditPlayer={(isOwnPlayer || !!userIsCreator) ? () => handleEditPlayer(p) : undefined}
            onRemovePlayer={(!!userIsCreator && !isOwnPlayer) ? () => removePlayer(p.id) : undefined}
            onSelfRemove={(isOwnPlayer && !userIsCreator) ? () => selfRemove(p.id) : undefined}
            canConfirmPayment={!!userIsCreator && Number(match?.price) > 0 && fieldPaymentStatusMap?.[p.id] !== 'confirmed'}
            onConfirmPayment={() => handleConfirmPayment(p.id)}
          />
        );
      })()}

      {/* MovePlayerModal — fallback descubrible al drag-drop */}
      {movePlayerModalPlayer && teams && match && (
        <MovePlayerModal
          isOpen={!!movePlayerModalPlayer}
          onClose={() => setMovePlayerModalPlayer(null)}
          player={movePlayerModalPlayer}
          teams={teams}
          substitutes={match.substitutes || []}
          team1Name={match.team1Name || 'Equipo 1'}
          team2Name={match.team2Name || 'Equipo 2'}
          maxPlayers={match.maxPlayers || 5}
          team1Color={match.team1Color || '#4CAF50'}
          team2Color={match.team2Color || '#2196F3'}
          onMovePlayer={movePlayer}
          onSwapPlayers={swapPlayers}
          onMoveToSubstitutes={async (playerId) => {
            const wasSubstitute = !!match.substitutes?.some((s) => s.id === playerId);
            await swapPlayers(playerId, playerId);
            showNotification(
              wasSubstitute ? 'Jugador pasó a titular' : 'Jugador pasó a suplentes',
              'success'
            );
          }}
        />
      )}

      {showReviewsList && (
        <ReviewsList
          matchId={id || ''}
          isVisible={showReviewsList}
          onClose={handleCloseReviewsList}
        />
      )}

      {/* Import List Modal */}
      <ImportListModal
        isOpen={showImportListModal}
        onClose={() => setShowImportListModal(false)}
        matchId={id || ''}
        onSuccess={handleImportSuccess}
      />

      {/* Custom Drag Preview */}
      <CustomDragPreview 
        player={draggedPlayer!} 
        isVisible={showDragPreview && !!draggedPlayer} 
      />

      {/* Share Modal */}
      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        matchId={id || ''}
        matchTitle={match ? `${match.team1Name} vs ${match.team2Name}` : 'Partido'}
        matchDate={match?.date}
        matchTime={match?.time}
        matchLocation={match?.location}
        matchPlayersCount={match ? (match.players?.filter((p: any) => !p.isSubstitute).length ?? 0) : undefined}
        matchMaxPlayers={match?.maxPlayers ? match.maxPlayers * 2 : undefined}
        onShowNotification={showNotification}
      />

      {/* Invite Player Modal (empty slot click on field) */}
      <InvitePlayerModal
        isOpen={showInvitePlayerModal}
        onClose={() => setShowInvitePlayerModal(false)}
        matchId={id || ''}
        matchTitle={match ? `${match.team1Name} vs ${match.team2Name}` : 'Partido'}
        isOrganizer={!!userIsCreator}
        isPlayerInMatch={!!isUserInMatch}
        matchStatus={match?.status}
        onInviteSuccess={() => { fetchMatch(); }}
        onShowNotification={showNotification}
      />

      {/* Team Suggestions Modal */}
      {showTeamSuggestionsModal && userIsCreator && (
        <TeamSuggestionsModal
          suggestions={teamSuggestions}
          team1Name={match?.team1Name || 'Equipo 1'}
          team2Name={match?.team2Name || 'Equipo 2'}
          onApply={handleApplySuggestion}
          onRefresh={handleSuggestTeams}
          onClose={() => {
            analytics.track('team_suggest_dismissed', { matchId: id });
            setShowTeamSuggestionsModal(false);
          }}
          loading={loadingSuggestions}
          showSkillWarning={
            (match?.players?.length ?? 0) > 0 &&
            (match?.players ?? []).every(p => p.organizerSkillScore == null)
          }
        />
      )}

      {/* Guest Requests Modal - organizer only */}
      {userIsCreator && (
        <GuestRequestsModal
          isOpen={showGuestRequestsModal}
          matchId={id || ''}
          onClose={() => setShowGuestRequestsModal(false)}
          onRequestsUpdated={() => {
            fetchMatch();
            fetchPendingGuestRequestsCount();
          }}
        />
      )}

      {/* Public match join applications - organizer only */}
      {userIsCreator && match.isPublic && (
        <MatchJoinApplicationsModal
          isOpen={showJoinApplicationsModal}
          matchId={id || ''}
          onClose={() => setShowJoinApplicationsModal(false)}
          onUpdated={() => {
            fetchMatch();
          }}
        />
      )}

      {/* Manage Guests Modal - propose (all players) + direct add (organizer) */}
      {(userIsCreator || isUserInMatch) && (
        <ManageGuestsModal
          isOpen={showManageGuestsModal}
          matchId={id || ''}
          isOrganizer={userIsCreator}
          onClose={() => setShowManageGuestsModal(false)}
          onGuestAdded={() => {
            setShowManageGuestsModal(false);
            fetchMatch();
            fetchPendingGuestRequestsCount();
          }}
          onShowNotification={showNotification}
        />
      )}

    </div>
  );
};


export default ViewMatch; 
